import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'
import { Upload, FileText, X, AlertCircle, CheckCircle2, FileSpreadsheet, Settings } from 'lucide-react'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { DriverMaster } from '@/types/driverMaster'
import { Vehicle } from '@/types/vehicle'
import { CompensationTargetType } from '@/types/compensation'
import { parseMoneyAmount } from '@/lib/money'
import * as XLSX from 'xlsx'

export interface CompensationFileUploadData {
  driverId?: string
  vehicleId?: string
  driverName?: string
  vehicleName?: string
  amount: number
  originalRowCount: number // How many rows were summed to get this amount
}

interface CompensationFileUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDataUploaded: (data: CompensationFileUploadData[]) => void
  targetType: CompensationTargetType
  drivers: DriverMaster[]
  vehicles: Vehicle[]
}

interface ParsedRow {
  identifier: string // name or registration number
  amount: number
  rowIndex: number
}

interface MatchResult extends CompensationFileUploadData {
  matched: boolean
  error?: string
}

interface ManualColumnMapping {
  identifierColumn: number
  amountColumn: number
  headerRow: number
}

export function CompensationFileUploadDialog({
  open,
  onOpenChange,
  onDataUploaded,
  targetType,
  drivers,
  vehicles
}: CompensationFileUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processedData, setProcessedData] = useState<MatchResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Manual mapping states (always required)
  const [rawFileData, setRawFileData] = useState<any[][] | null>(null)
  const [manualMapping, setManualMapping] = useState<ManualColumnMapping>({
    identifierColumn: 0,
    amountColumn: 1,
    headerRow: 0
  })
  const [showMappingStep, setShowMappingStep] = useState(false)

  // Helper function to normalize text for exact matching
  const normalizeText = (text: string): string => {
    return text.trim()
  }

  // Parse amount from file input; negative amounts are rejected
  // (the compensation/deduction type carries the sign in this domain)
  const parseAmount = (value: unknown): number | null => {
    const parsed = parseMoneyAmount(value)
    return parsed === null || parsed < 0 ? null : parsed
  }


  // Extract registration number from "Vehicle: ABC123" format
  const extractRegistrationNumber = (text: string): string => {
    const match = text.match(/Vehicle:\s*([A-Z0-9]+)/i)
    if (match) {
      return match[1].toUpperCase()
    }
    return text.trim().toUpperCase()
  }

  // Exact match function for identifiers
  const exactMatch = (text1: string, text2: string): boolean => {
    return normalizeText(text1) === normalizeText(text2)
  }

  // Match identifier to driver or vehicle (exact match only)
  const matchIdentifier = (identifier: string): { type: 'driver' | 'vehicle', id: string, name: string } | null => {
    if (targetType === 'driver') {
      // Exact match against driver names
      for (const driver of drivers) {
        if (driver.primaryName && exactMatch(identifier, driver.primaryName)) {
          return { type: 'driver', id: driver.id, name: driver.primaryName }
        }

        // Note: No alternative names check in simplified version
      }
    } else {
      // Extract registration number and exact match
      const extractedIdentifier = extractRegistrationNumber(identifier)

      for (const vehicle of vehicles) {
        const vehicleReg = vehicle.carRegNumber.toUpperCase()
        if (extractedIdentifier === vehicleReg) {
          return { type: 'vehicle', id: vehicle.id, name: vehicle.carRegNumber }
        }
      }
    }

    return null
  }

  // Process file with manual column mapping
  const processFileWithMapping = (data: any[][], mapping: ManualColumnMapping) => {
    const { identifierColumn, amountColumn, headerRow } = mapping

    // Get data rows (skip header and any rows before it)
    const dataRows = data.slice(headerRow + 1).filter(row =>
      row && row.some(cell => cell !== '' && cell !== null && cell !== undefined)
    )

    if (dataRows.length === 0) {
      throw new Error('Inga datarader hittades efter vald rubrikrad')
    }

    // Parse rows with manual mapping
    const parsedRows: ParsedRow[] = []

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i]
      const identifier = row[identifierColumn]?.toString()?.trim()
      const amountValue = row[amountColumn]

      if (!identifier) continue

      const amount = parseAmount(amountValue)
      if (amount === null || amount <= 0) continue

      parsedRows.push({
        identifier,
        amount,
        rowIndex: headerRow + i + 2 // Adjust for header row and 1-based indexing
      })
    }

    if (parsedRows.length === 0) {
      throw new Error('Inga giltiga rader med namn/registreringsnummer och belopp hittades med valda kolumner')
    }

    // Group by identifier and sum amounts
    const groupedData = new Map<string, { amount: number, rowCount: number, identifier: string }>()

    for (const row of parsedRows) {
      const key = normalizeText(row.identifier)
      const existing = groupedData.get(key)

      if (existing) {
        existing.amount += row.amount
        existing.rowCount += 1
      } else {
        groupedData.set(key, {
          amount: row.amount,
          rowCount: 1,
          identifier: row.identifier
        })
      }
    }

    // Match identifiers to drivers/vehicles
    const matchResults: MatchResult[] = []

    for (const [, group] of groupedData) {
      const match = matchIdentifier(group.identifier)

      if (match) {
        const result: MatchResult = {
          amount: group.amount,
          originalRowCount: group.rowCount,
          matched: true
        }

        if (match.type === 'driver') {
          result.driverId = match.id
          result.driverName = match.name
        } else {
          result.vehicleId = match.id
          result.vehicleName = match.name
        }

        matchResults.push(result)
      } else {
        matchResults.push({
          amount: group.amount,
          originalRowCount: group.rowCount,
          matched: false,
          error: `Ingen match för "${group.identifier}"`
        })
      }
    }

    return matchResults
  }

  // Process uploaded file
  const processFile = async (file: File) => {
    setIsProcessing(true)
    setError(null)
    setProcessedData(null)

    try {
      // Parse file
      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]]

      // Convert to array of arrays
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false }) as any[][]

      if (jsonData.length === 0) {
        throw new Error('Filen är tom')
      }

      // Store raw file data and show mapping step
      setRawFileData(jsonData)
      setShowMappingStep(true)

      // Set default mapping to first columns
      setManualMapping({
        identifierColumn: 0,
        amountColumn: jsonData[0]?.length > 1 ? 1 : 0,
        headerRow: 0
      })

    } catch (error) {
      console.error('Error processing file:', error)
      setError(error instanceof Error ? error.message : 'Det gick inte att bearbeta filen')
    } finally {
      setIsProcessing(false)
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      setSelectedFile(file)
      setError(null)
      setProcessedData(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  })

  const handleProcessFile = () => {
    if (selectedFile) {
      processFile(selectedFile)
    }
  }

  const handleImportData = () => {
    if (processedData) {
      const matchedData = processedData.filter(item => item.matched) as CompensationFileUploadData[]
      onDataUploaded(matchedData)
      onOpenChange(false)

      // Reset state
      setSelectedFile(null)
      setProcessedData(null)
      setError(null)
    }
  }

  // Handle manual column mapping
  const handleManualMapping = () => {
    if (!rawFileData) return

    try {
      const results = processFileWithMapping(rawFileData, manualMapping)
      setProcessedData(results)
      setShowMappingStep(false)
      setError(null)
    } catch (error) {
      console.error('Error with manual mapping:', error)
      setError(error instanceof Error ? error.message : 'Fel vid manuell mappning')
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
    // Reset state
    setSelectedFile(null)
    setProcessedData(null)
    setError(null)
    setShowMappingStep(false)
    setRawFileData(null)
    setManualMapping({
      identifierColumn: 0,
      amountColumn: 1,
      headerRow: 0
    })
  }

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  const matchedCount = processedData?.filter(item => item.matched).length || 0
  const unmatchedCount = processedData?.filter(item => !item.matched).length || 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Ladda upp ersättningar/avdrag från fil
          </DialogTitle>
          <DialogDescription>
            Ladda upp en CSV eller Excel-fil med {targetType === 'driver' ? 'förarens namn' : 'bilens registreringsnummer'} och belopp.
            Om samma {targetType === 'driver' ? 'förare' : 'bil'} finns på flera rader summeras beloppen automatiskt.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto">
          {/* Manual Column Mapping */}
          {showMappingStep && rawFileData && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-amber-800">
                <Settings className="h-5 w-5" />
                <h3 className="font-medium">Manuell kolumnmappning</h3>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Välj vilka kolumner som innehåller {targetType === 'driver' ? 'förarnamn' : 'registreringsnummer'} och belopp,
                  samt vilken rad som innehåller kolumnrubriker.
                </AlertDescription>
              </Alert>

              {/* Column Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Rubrikrad</Label>
                  <Combobox
                    options={rawFileData.slice(0, 10).map((_, index) => ({
                      value: index.toString(),
                      label: `Rad ${index + 1}`
                    }))}
                    value={manualMapping.headerRow.toString()}
                    onValueChange={(value) =>
                      setManualMapping(prev => ({ ...prev, headerRow: parseInt(value) }))
                    }
                    placeholder="Välj rad"
                    searchPlaceholder="Sök rad..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>{targetType === 'driver' ? 'Namn' : 'Registreringsnummer'}</Label>
                  <Combobox
                    options={(rawFileData[manualMapping.headerRow] || []).map((header: any, index: number) => ({
                      value: index.toString(),
                      label: `Kolumn ${index + 1}: ${String(header || '(tom)')}`
                    }))}
                    value={manualMapping.identifierColumn.toString()}
                    onValueChange={(value) =>
                      setManualMapping(prev => ({ ...prev, identifierColumn: parseInt(value) }))
                    }
                    placeholder="Välj kolumn"
                    searchPlaceholder="Sök kolumn..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Belopp</Label>
                  <Combobox
                    options={(rawFileData[manualMapping.headerRow] || []).map((header: any, index: number) => ({
                      value: index.toString(),
                      label: `Kolumn ${index + 1}: ${String(header || '(tom)')}`
                    }))}
                    value={manualMapping.amountColumn.toString()}
                    onValueChange={(value) =>
                      setManualMapping(prev => ({ ...prev, amountColumn: parseInt(value) }))
                    }
                    placeholder="Välj kolumn"
                    searchPlaceholder="Sök kolumn..."
                  />
                </div>
              </div>

              {/* Preview Table */}
              <div className="space-y-2">
                <Label>Förhandsvisning (första 5 raderna)</Label>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-2 py-1 text-left font-medium">Rad</th>
                          {rawFileData[manualMapping.headerRow]?.map((header: any, index: number) => (
                            <th
                              key={index}
                              className={`px-2 py-1 text-left font-medium ${
                                index === manualMapping.identifierColumn
                                  ? 'bg-blue-100 text-blue-800'
                                  : index === manualMapping.amountColumn
                                    ? 'bg-green-100 text-green-800'
                                    : ''
                              }`}
                            >
                              {String(header || `Kolumn ${index + 1}`)}
                            </th>
                          )) || []}
                        </tr>
                      </thead>
                      <tbody>
                        {rawFileData.slice(manualMapping.headerRow, manualMapping.headerRow + 6).map((row: any[], rowIndex: number) => (
                          <tr
                            key={rowIndex}
                            className={`border-b ${rowIndex === 0 ? 'bg-yellow-50' : ''}`}
                          >
                            <td className="px-2 py-1 text-muted-foreground">
                              {manualMapping.headerRow + rowIndex + 1}
                              {rowIndex === 0 && ' (rubrik)'}
                            </td>
                            {row?.map((cell: any, cellIndex: number) => (
                              <td
                                key={cellIndex}
                                className={`px-2 py-1 ${
                                  cellIndex === manualMapping.identifierColumn
                                    ? 'bg-blue-50 text-blue-900'
                                    : cellIndex === manualMapping.amountColumn
                                      ? 'bg-green-50 text-green-900'
                                      : ''
                                }`}
                              >
                                {String(cell || '')}
                              </td>
                            )) || []}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowMappingStep(false)}>
                  Tillbaka
                </Button>
                <Button onClick={handleManualMapping}>
                  Bearbeta data
                </Button>
              </div>
            </div>
          )}

          {/* File Upload Zone */}
          {!processedData && !showMappingStep && (
            <div>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary bg-primary/5'
                    : selectedFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                }`}
              >
                <input {...getInputProps()} />

                {selectedFile ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center">
                      {selectedFile.name.endsWith('.csv') ? (
                        <FileText className="h-12 w-12 text-green-500" />
                      ) : (
                        <FileSpreadsheet className="h-12 w-12 text-green-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-green-700">
                        {selectedFile.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFile(null)
                        setError(null)
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Ta bort
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center">
                      <Upload className="h-12 w-12 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {isDragActive ? 'Släpp filen här' : 'Dra och släpp fil här'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        eller klicka för att välja fil
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        CSV, XLS, XLSX (max 10MB)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* File format instructions */}
              <Alert className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Filformat:</strong> Filen ska innehålla kolumner för{' '}
                  <strong>{targetType === 'driver' ? 'förarens namn (exakt matchning)' : 'bilens registreringsnummer'}</strong> och{' '}
                  <strong>belopp</strong>. Du kommer att välja vilka kolumner som ska användas.
                  {targetType === 'vehicle' && (
                    <><br /><strong>Tips:</strong> Format "Vehicle: ABC123" extraheras automatiskt till "ABC123".</>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Processing State */}
          {isProcessing && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center space-y-2">
                <SpinnerIcon size="lg" />
                <p className="text-sm text-muted-foreground">Bearbetar fil...</p>
              </div>
            </div>
          )}

          {/* Results Display */}
          {processedData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-medium">Matchade</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">{matchedCount}</p>
                </div>

                {unmatchedCount > 0 && (
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-2 text-yellow-800">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-medium">Omatchade</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-900">{unmatchedCount}</p>
                  </div>
                )}
              </div>

              {/* Matched Results */}
              {matchedCount > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Matchade {targetType === 'driver' ? 'förare' : 'bilar'}:</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg max-h-40 overflow-y-auto">
                    {processedData.filter(item => item.matched).map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-2 border-b border-green-200 last:border-b-0">
                        <span className="font-medium">
                          {targetType === 'driver' ? item.driverName : item.vehicleName}
                        </span>
                        <div className="text-right">
                          <span className="font-bold">{item.amount.toLocaleString('sv-SE')} kr</span>
                          {item.originalRowCount > 1 && (
                            <div className="text-xs text-muted-foreground">
                              ({item.originalRowCount} rader summerade)
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Results */}
              {unmatchedCount > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-yellow-800">Omatchade rader:</h4>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg max-h-32 overflow-y-auto">
                    {processedData.filter(item => !item.matched).map((item, index) => (
                      <div key={index} className="p-2 border-b border-yellow-200 last:border-b-0">
                        <div className="text-sm text-yellow-800">{item.error}</div>
                        <div className="text-xs text-muted-foreground">Belopp: {item.amount.toLocaleString('sv-SE')} kr</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-yellow-600 mt-2">
                    Omatchade rader kommer inte att importeras.
                  </p>

                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={handleCancel}>
            Avbryt
          </Button>

          {!processedData && !showMappingStep && selectedFile && !isProcessing && (
            <Button onClick={handleProcessFile}>
              Nästa: Välj kolumner
            </Button>
          )}

          {processedData && matchedCount > 0 && (
            <Button onClick={handleImportData}>
              Importera {matchedCount} {targetType === 'driver' ? 'förare' : 'bilar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}